import { create } from "zustand";
import { devtools } from "zustand/middleware";

// Selector functions — mirror the Redux selectors in redux/selectors/adminData.js
// Usage: useAdminDataStore(selectCurrentGroupData)

// Stable fallback — must be a module-level constant so selectors always return
// the same reference when data is absent (avoids useSyncExternalStore infinite loop)
const EMPTY_OBJECT = {};

export const selectCurrentGroupData = (state) =>
  state.allGroups[state.currentGroup]?.data ?? undefined;

export const selectAllGroups = (state) => state.allGroups;

export const selectCurrentGroupId = (state) => state.currentGroup;

export const selectDashboardType = (state) => state.dashboardType;

export const selectGettingStartedItemStatus = (state) =>
  state.allGroups[state.currentGroup]?.data?.gettingStarted ?? EMPTY_OBJECT;

export const selectGettingStartedExpanded = (state) =>
  state.gettingStartedExpanded ?? false;

export const selectIsDashboardLoading = (state) =>
  state.isDashboardLoading ?? false;

export const selectRedirectPath = (state) => state.redirectPath ?? "";

const useAdminDataStore = create(
  devtools(
    (set, get) => ({
      currentGroup: "",
      allGroups: {},
      connection: undefined,
      dashboardType: "admin",
      gettingStartedExpanded: false,
      isDashboardLoading: false,
      redirectPath: "",

      // Mirrors UPDATE_ADMIN_GROUPS dispatch in redux/actions/user.js
      setAdminGroups: (groups, connection) => {
        const allGroups = {};
        groups.forEach((group) => {
          allGroups[group.id] = {
            currentContents: { status: "process", records: undefined },
            pastContents: { status: "process", records: undefined },
            members: { status: "process", records: undefined },
            data: group,
          };
        });

        const groupNameKeyMap = {};
        Object.entries(allGroups).forEach(([key, group]) => {
          groupNameKeyMap[group.data.groupName] = key;
        });

        const firstGroupName = Object.keys(groupNameKeyMap).sort((a, b) =>
          a.localeCompare(b)
        )[0];

        const currentGroup =
          window.localStorage.getItem("currentGroup") ||
          get().currentGroup ||
          groupNameKeyMap[firstGroupName];

        set({ allGroups, currentGroup, connection });
      },

      // Mirrors GROUP_CHANGE dispatch in redux/actions/adminData.js (adminGroupChange)
      setCurrentGroup: (groupId, dashboardType = "admin") => {
        window.localStorage.setItem("currentGroup", groupId);
        set({ currentGroup: groupId, dashboardType });
      },

      // Mirrors REFRESH_GROUP_DATA dispatch in redux/actions/adminData.js (refreshGroupData)
      updateGroupData: (groupId, data) => {
        const { allGroups } = get();
        if (!groupId || !data || !allGroups[groupId]) return;
        set({
          allGroups: {
            ...allGroups,
            [groupId]: {
              ...allGroups[groupId],
              data: { ...data },
            },
          },
        });
      },

      // Mirrors SET_GETTING_STARTED_ITEM_STATUS
      setGettingStartedItemStatus: (groupId, guideId, topicId, value) => {
        const { allGroups } = get();
        let groupData = allGroups[groupId]?.data;
        if (!groupData) return;

        const currentCompletedCount =
          groupData.gettingStarted?.[guideId]?.completedItems ?? 0;
        const totalCompletedCount =
          groupData.gettingStarted?.totalCompletedItems ?? 0;

        if (value === groupData.gettingStarted?.[guideId]?.topics?.[topicId])
          return;

        const newCompletedCount = value
          ? currentCompletedCount + 1
          : currentCompletedCount - 1;
        const newTotalCompletedCount = value
          ? totalCompletedCount + 1
          : totalCompletedCount - 1;

        const updatedGroupData = {
          ...groupData,
          gettingStarted: {
            ...groupData.gettingStarted,
            totalCompletedItems: newTotalCompletedCount,
            [guideId]: {
              ...groupData.gettingStarted?.[guideId],
              completedItems: newCompletedCount,
              topics: {
                ...groupData.gettingStarted?.[guideId]?.topics,
                [topicId]: value,
              },
            },
          },
        };

        set({
          allGroups: {
            ...allGroups,
            [groupId]: {
              ...allGroups[groupId],
              data: updatedGroupData,
            },
          },
        });
      },

      setGettingStartedExpanded: (value) =>
        set({ gettingStartedExpanded: value }),

      setDashboardLoading: (loading) => set({ isDashboardLoading: loading }),

      setDashboardType: (type) => set({ dashboardType: type }),

      setRedirectPath: (path) => set({ redirectPath: path }),

      reset: () =>
        set({
          currentGroup: "",
          allGroups: {},
          connection: undefined,
          dashboardType: "admin",
          gettingStartedExpanded: false,
          isDashboardLoading: false,
          redirectPath: "",
        }),
    }),
    { name: "AdminData" }
  )
);

export default useAdminDataStore;
